/**
 * AI Operations Division team data.
 *
 * Five members — three Senior AI Analysts, one Head of QA, one Director —
 * powering every automated workflow at NoJo Enterprise (ETC, toll
 * receipts, planned exchange). Used by:
 *   - /:lang/our-team (OurTeamPage)
 *   - <AITeamPreviewStrip /> on the homepage
 *   - Editorial group-photo breaks on About + Services + Our Team
 *
 * Each portrait is set in Yokosuka — NoJo's home base — so the imagery
 * stays cohesive with the rest of the site. Group photos rotate across
 * five iconic Japan locations.
 */

// Individual portraits (NoJo-branded, Yokosuka backgrounds).
const PORTRAITS = {
  sora: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/03qzbfjc_sora.png",
  rin: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/8ex62nkn_rin.png",
  yuki: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/zeym0q3n_yuki.png",
  hana: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/yuk4zbp0_hana.png",
  aoi: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/g0w15o6c_aoi.png",
};

// Five group photos at iconic Japan locations.
export const TEAM_GROUP_PHOTOS = [
  {
    id: "mtfuji",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/h6te20ge_mtfuji.png",
    locationJa: "富士山",
    locationEn: "Mt. Fuji",
  },
  {
    id: "tokyo",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/afxxb389_tokyo.png",
    locationJa: "東京スカイツリー",
    locationEn: "Tokyo Skytree",
  },
  {
    id: "shibuya",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/4gqm86p9_shibuya.png",
    locationJa: "渋谷",
    locationEn: "Shibuya",
  },
  {
    id: "osaka",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/frowuer4_osaka.png",
    locationJa: "大阪・道頓堀",
    locationEn: "Osaka · Dotonbori",
  },
  {
    id: "temple",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/yfjy6s4h_temple.png",
    locationJa: "京都・清水寺",
    locationEn: "Kyoto · Kiyomizu-dera",
  },
];

/**
 * AI development workspace photos. Used to illustrate "how NoJo actually
 * runs" — the trained five-member LLM team sitting at the data terminals,
 * dashboards, and the AI control room. Use on AboutPage, OurTeamPage and
 * the JapanETCcard detail page (since the AI team is the backbone of how
 * the ETC pipeline processes).
 */
export const TEAM_WORKSPACE_PHOTOS = [
  {
    id: "ai-night",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/r13xql0m_6.png",
    captionEn: "AI control room · evening shift",
    captionJa: "AI コントロールルーム · 夜間シフト",
  },
  {
    id: "ai-ops",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/76al6qi5_7.png",
    captionEn: "AI Operations dashboard · model performance 98.7%",
    captionJa: "AI オペレーション・ダッシュボード · モデル性能 98.7%",
  },
  {
    id: "ai-workflow",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/ldrgky8i_8.png",
    captionEn: "Designing the AI workflow that powers every service",
    captionJa: "全サービスを支えるAIワークフローを設計",
  },
  {
    id: "ai-elevate",
    src: "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/05f0ay7p_9.png",
    captionEn: "Innovate · Automate · Elevate",
    captionJa: "革新 · 自動化 · 向上",
  },
];

// Brand palette per role.
export const ROLE_THEME = {
  director: { hex: "#F59E0B", labelEn: "DIRECTOR", labelJa: "ディレクター" },
  qa: { hex: "#0E5FB0", labelEn: "QA LEAD", labelJa: "QA リード" },
  analyst: { hex: "#2F8F3C", labelEn: "AI ANALYST", labelJa: "AI アナリスト" },
};

/**
 * Roster — ordered by org structure: Director → QA Lead → 3 Analysts.
 * Bios are written for NoJo Enterprise (not the sister sites) to keep
 * the corporate framing consistent.
 */
export const AI_TEAM = [
  {
    slug: "sora",
    nameEn: "Sora",
    nameJa: "ソラ",
    role: "director",
    titleEn: "Director of AI Operations",
    titleJa: "AIオペレーション・ディレクターロール",
    specEn: null,
    specJa: null,
    portrait: PORTRAITS.sora,
    bioEn:
      "Sora oversees every AI-driven workflow across NoJo Enterprise — from ETC card pipelines, to toll-record extraction, to the planned exchange service. She gives the final go-ahead on automated tasks before they reach customers, and coordinates the team so operations stay consistent across all three services.",
    bioJa:
      "ソラは、NoJo Enterprise全体のAI支援ワークフローにおいて、ETCカード、通行記録、準備中の両替サービスまで、処理状況を整理し、確認が必要な内容を人のチームへ渡す役割を担います。3つのサービス間で運用品質が一貫するよう、確認ポイントを整えます。",
    bulletsEn: [
      "Oversees every AI-driven workflow across NoJo",
      "Final quality approval on all automated outputs",
      "Coordinates the analyst team and routes urgent work",
      "Strategic planning for AI capability across services",
    ],
    bulletsJa: [
      "NoJoのAI業務フローを統括",
      "自動化された処理の最終品質承認",
      "アナリストチームの調整と緊急案件の割り振り",
      "サービス全体のAI能力に関する戦略立案",
    ],
  },
  {
    slug: "rin",
    nameEn: "Rin",
    nameJa: "リン",
    role: "qa",
    titleEn: "Head of Quality Assurance",
    titleJa: "品質確認責任者ロール",
    specEn: null,
    specJa: null,
    portrait: PORTRAITS.rin,
    bioEn:
      "Rin cross-checks every artifact the analyst team produces — ETC registrations, toll receipts, planned exchange records — before anything leaves NoJo. She resolves discrepancies, surfaces edge cases for human review (never auto-conclusions), and reports recurring patterns back to leadership.",
    bioJa:
      "リンは、アナリストロールが生成した成果物（ETC登録、通行記録、準備中の両替記録）を確認し、差異や確認が必要なケースを人によるレビューに回します。自動的な決定は行わず、繰り返し起こるパターンを経営層へ報告するための情報整理を支援します。",
    bulletsEn: [
      "Reviews and validates every AI team output",
      "Resolves discrepancies and edge cases",
      "Surfaces items for human review — never auto-conclusions",
      "Reports recurring patterns back to leadership",
    ],
    bulletsJa: [
      "AIチームの出力をすべて検証",
      "差異とエッジケースを解消",
      "人によるレビュー対象を提示（自動判断はしない）",
      "繰り返し起こるパターンを経営層に報告",
    ],
  },
  {
    slug: "yuki",
    nameEn: "Yuki",
    nameJa: "ユキ",
    role: "analyst",
    titleEn: "Senior AI Analyst",
    titleJa: "シニアAIアナリスト",
    specEn: "Specializes in OpenAI",
    specJa: "OpenAI を担当",
    portrait: PORTRAITS.yuki,
    bioEn:
      "Yuki handles data extraction and document processing across NoJo's services with precision. She works in pass-through mode — pulling, verifying, and organizing customer information so records stay accurate and up to date without anything being permanently stored that doesn't need to be.",
    bioJa:
      "ユキは、NoJoの各サービスにおけるデータ抽出と書類処理を支援します。パススルー方式で、顧客情報の取得・確認・整理を行い、不要な情報を長期保存しない設計を支えます。",
    bulletsEn: [
      "Data extraction and document processing",
      "Information verification and validation",
      "Record keeping and data organization",
      "Cross-referencing and accuracy checks",
    ],
    bulletsJa: [
      "データ抽出と書類処理",
      "情報の確認と検証",
      "記録管理とデータ整理",
      "相互参照と精度チェック",
    ],
  },
  {
    slug: "hana",
    nameEn: "Hana",
    nameJa: "ハナ",
    role: "analyst",
    titleEn: "Senior AI Analyst",
    titleJa: "シニアAIアナリスト",
    specEn: "Specializes in Gemini",
    specJa: "Gemini を担当",
    portrait: PORTRAITS.hana,
    bioEn:
      "Hana specializes in interpreting complex documents and structured forms — ETC registration packs, MEISAI raw exports, exchange compliance paperwork. Her attention to detail ensures even the most challenging files are processed correctly and rendered into bilingual JA/EN reports.",
    bioJa:
      "ハナは複雑な書類や構造化フォームの解釈を支援します。ETC申請書類、MEISAIの生データ、両替コンプライアンス書類など、難易度の高いファイルを整理し、日英二か国語のレポート作成につなげます。",
    bulletsEn: [
      "Complex document interpretation",
      "Form and record analysis",
      "Data accuracy and quality assurance",
      "Process improvement support",
    ],
    bulletsJa: [
      "複雑な書類の解釈",
      "フォームと記録の分析",
      "データ精度と品質保証",
      "プロセス改善のサポート",
    ],
  },
  {
    slug: "aoi",
    nameEn: "Aoi",
    nameJa: "アオイ",
    role: "analyst",
    titleEn: "Senior AI Analyst",
    titleJa: "シニアAIアナリスト",
    specEn: "Specializes in Claude",
    specJa: "Claude を担当",
    portrait: PORTRAITS.aoi,
    bioEn:
      "Aoi brings a detail-oriented approach to every task. He classifies information methodically, catches inconsistencies before they propagate downstream, and handles multi-format files — PDF, CSV, scanned image, structured JSON — without losing the integrity of the underlying record.",
    bioJa:
      "アオイは細部の確認を支援し、情報を体系的に分類して、下流工程に影響する可能性のある不整合を確認候補として提示します。PDF、CSV、スキャン画像、構造化JSONなど、多形式のファイルを記録の整合性に配慮しながら処理します。",
    bulletsEn: [
      "Data processing and classification",
      "Anomaly and inconsistency detection",
      "Multi-format document handling",
      "Workflow automation support",
    ],
    bulletsJa: [
      "データ処理と分類",
      "異常値と不整合の検出",
      "複数形式の書類処理",
      "ワークフロー自動化のサポート",
    ],
  },
];

export const TEAM_MANIFESTO = {
  eyebrowEn: "AI Operations Division",
  eyebrowJa: "AI支援オペレーション",
  titleEn: "Our AI Team",
  titleJa: "AI支援ワークフロー",
  leadEn:
    "Our AI Operations team works around the clock to verify thousands of vehicle documents, toll records and customer files with precision and accuracy. Each member brings unique expertise — and a clearly defined seat in the workflow — so every customer's documentation is thoroughly reviewed and validated before it ships.",
  leadJa:
    "NoJoのAI支援ワークフローは、車両関連書類・通行記録・お客様ファイルの確認作業を支えています。各モデルが明確な役割を持ち、必要な情報を整理し、人による確認につなげます。",
};
